<script setup lang="ts">
import FormSubmissionAnswerList from '@/components/forms/FormSubmissionAnswerList.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { formatShortDate } from '~/lib/formatters';
import type { FormSubmission, PortalFormRequest } from '~/types/models';

const props = withDefaults(defineProps<{
  open: boolean;
  submission?: FormSubmission | null;
  request?: PortalFormRequest | null;
  clientHref?: string;
}>(), {
  submission: null,
  request: null,
  clientHref: '',
});

const emit = defineEmits<{
  'update:open': [value: boolean];
}>();

const isDraft = computed(() => Boolean(props.request));
const answers = computed(() => props.request?.draftAnswers || props.submission?.answers || []);
const title = computed(() => (
  props.request?.clientName
  || props.submission?.respondentName
  || props.submission?.respondentEmail
  || 'Anonymous response'
));
const formName = computed(() => props.request?.formName || props.submission?.formName || 'Unknown form');
const formKind = computed(() => props.request?.formKind || props.submission?.kind || 'lead_capture');
const contact = computed(() => props.submission?.respondentEmail || props.submission?.respondentPhone || 'No contact detail captured');
const dateLabel = computed(() => formatShortDate(
  props.request?.draftUpdatedAt
  || props.request?.updatedAt
  || props.submission?.createdAt,
));
</script>

<template>
  <Sheet :open="open" @update:open="emit('update:open', $event)">
    <SheetContent side="right" class="!flex h-full !w-full max-w-none flex-col gap-0 overflow-hidden bg-background p-0 sm:!max-w-[800px]">
      <SheetHeader class="shrink-0 border-b border-border/70 px-6 py-5 pr-14 text-left">
        <div class="flex flex-wrap items-center gap-2">
          <Badge :variant="isDraft ? 'secondary' : 'outline'">{{ isDraft ? 'In progress' : 'Submitted' }}</Badge>
          <Badge variant="outline">{{ formKind === 'questionnaire' ? 'Questionnaire' : 'Enquiry form' }}</Badge>
        </div>
        <SheetTitle class="text-2xl font-semibold tracking-tight">
          <NuxtLink v-if="clientHref" :to="clientHref" class="hover:underline">{{ title }}</NuxtLink>
          <span v-else>{{ title }}</span>
        </SheetTitle>
        <SheetDescription class="text-sm leading-6">
          {{ isDraft ? 'Review the answers saved so far. The client can still change them before submitting.' : `Response to ${formName}.` }}
        </SheetDescription>
      </SheetHeader>

      <div class="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-6 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <section class="grid gap-5 py-6 sm:grid-cols-3">
          <div>
            <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Form</p>
            <p class="mt-1 text-sm font-medium">{{ formName }}</p>
          </div>
          <div>
            <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{{ isDraft ? 'Client' : 'Contact' }}</p>
            <p class="mt-1 text-sm font-medium">{{ isDraft ? (request?.clientName || 'Client') : contact }}</p>
          </div>
          <div>
            <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{{ isDraft ? 'Last saved' : 'Submitted' }}</p>
            <p class="mt-1 text-sm font-medium">{{ dateLabel }}</p>
          </div>
        </section>

        <section class="border-t border-border/70 py-6">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 class="text-sm font-semibold">Captured answers</h3>
              <p class="mt-1 text-sm leading-6 text-muted-foreground">
              {{ isDraft ? 'These answers are autosaved and may not be complete yet.' : 'The final answers supplied by the respondent.' }}
              </p>
            </div>
            <Badge variant="outline">{{ answers.length }} answer{{ answers.length === 1 ? '' : 's' }}</Badge>
          </div>
          <div class="mt-4">
            <FormSubmissionAnswerList
              :answers="answers"
              surface="plain"
              :empty-message="isDraft ? 'The client has opened this form but has not entered an answer yet.' : 'No answers were captured for this submission.'"
            />
          </div>
        </section>
      </div>

      <SheetFooter class="shrink-0 border-t border-border/70 bg-background/95 px-6 py-4 backdrop-blur sm:justify-between">
        <Button variant="outline" @click="emit('update:open', false)">Close</Button>
        <Button v-if="clientHref" as-child><NuxtLink :to="clientHref">Open client record</NuxtLink></Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>
